import { DOCUMENT, formatDate } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  NgZone,
  OnInit,
  runInInjectionContext,
  signal,
  Signal,
} from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { Meta } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, NavigationError, Router, RouterOutlet } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { setTag as sentrySetTag } from "@sentry/angular";
import { NgHttpLoaderComponent } from "ng-http-loader";
import { distinctUntilChanged, filter, finalize, firstValueFrom, map, pairwise, startWith } from "rxjs";
import { ToastsComponent } from "src/app/core/components/toasts/toasts.component";
import { environment } from "src/environments/environment";
import { AuthenticationService } from "./core/authentication/authentication.service";
import { SessionService } from "./core/authentication/session.service";
import { UserSettingsService } from "./core/authentication/user-settings.service";
import { AppSettings } from "./core/config/app-settings.config";
import { buildDate } from "./core/constants/build-date";
import { getLanguageDirection } from "./core/constants/language-direction";
import { EnvironmentId } from "./core/enums/environment-id.enum";
import { SentryTag } from "./core/enums/sentry-tag.enum";
import { ToastType } from "./core/enums/toast-type.enum";
import { arrayShallowClone } from "./core/functions/array-helpers";
import { getHtmlLangValue } from "./core/functions/get-html-lang-value";
import { isIn } from "./core/functions/is-in";
import { langTagToAppLang } from "./core/functions/lang-tag-to-app-lang";
import { AppRouteData } from "./core/models/app-route.model";
import { OnlineService, ToastService } from "./core/services";
import { MyRegSpinnerComponent } from "./shared/components/my-reg-spinner/my-reg-spinner.component";
import { CookieStorageService } from "./shared/services/cookie-storage.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  imports: [RouterOutlet, NgHttpLoaderComponent, ToastsComponent],
  providers: [OnlineService],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    /* eslint-disable @typescript-eslint/naming-convention */
    "(window:pageshow)": "onWindowPageShow($event)",
    /* eslint-enable @typescript-eslint/naming-convention */
  },
})
export class AppComponent implements OnInit, AfterViewInit {
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly injector = inject(Injector);
  protected readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly translate = inject(TranslateService);
  protected readonly router = inject(Router);
  protected readonly meta = inject(Meta);
  protected readonly online = inject(OnlineService);
  protected readonly session = inject(SessionService);
  protected readonly cookieStorage = inject(CookieStorageService);
  protected readonly auth = inject(AuthenticationService);
  protected readonly userSettings = inject(UserSettingsService);
  protected readonly toast = inject(ToastService);
  protected readonly ngZone = inject(NgZone);
  protected readonly document = inject(DOCUMENT, { optional: true }) ?? window.document;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public readonly MyRegSpinnerComponent: typeof MyRegSpinnerComponent = MyRegSpinnerComponent;

  public readonly filteredUrlPatterns: string[] = arrayShallowClone(AppSettings.filteredUrlPatterns);

  private readonly defaultDescriptionKey: string = "STARTUP.CLAIM_1";

  public readonly labels = <const>{
    toast: <const>{
      closeButtonAriaLabel: ((): Signal<string> => {
        const translation = toSignal<unknown>(this.translate.stream("ARIA_LABEL_GENERAL.CLOSE"));
        return computed(() => translation()?.toString() ?? "Close");
      })(),
    },
  };

  public onWindowPageShow(event: PageTransitionEvent): void {
    // Cache busting when user click the browser's back button outside this webapp
    if (event.persisted) {
      this.document.body.dataset["preventWindowUnload"] = "false";
      window.location.reload();
    }
  }

  public ngOnInit(): void {
    console.log(
      "Environment ➡",
      (isIn(environment.id, EnvironmentId) ? EnvironmentId[environment.id] : null) ?? environment.id,
      formatDate(buildDate, "yyyyMMddHHmmssSSS", "en-US")
    );

    this.outdatedJavascriptVersionWarning();
    this.watchRouteChanges();
    this.watchLangChange();
    this.watchAndSetUserLang();
    this.handleChunkLoadErrors();

    // Unregister service workers if they're disabled
    if (!AppSettings.enableServiceWorker) {
      this.unregisterServiceWorkers();
    }

    if (typeof window?.CSS?.supports === "function") {
      sentrySetTag(SentryTag.CssDisplayWebkitBox, CSS.supports("display", "-webkit-box"));
      sentrySetTag(SentryTag.CssContainerType, CSS.supports("container-type", "size"));
      sentrySetTag(SentryTag.CssContainerName, CSS.supports("container-name", "main"));
    }
  }

  public ngAfterViewInit(): void {
    // Remove app bootstrap loading animation
    this.ngZone.runOutsideAngular(() => {
      this.document.body.querySelector("#app-init-loading")?.remove();
      this.document.head.querySelector("link#app-init-loading-css")?.remove();
    });

    // Sets alt text for the required indicator
    this.setAltRequiredIndicator();
  }

  private async unregisterServiceWorkers(): Promise<void> {
    // Changes applied after reload
    const serviceWorker: ServiceWorkerContainer | null = window?.navigator?.serviceWorker ?? null;
    if (!serviceWorker) return;
    try {
      for (const registration of await serviceWorker.getRegistrations()) {
        await registration.unregister().catch((err: unknown) => console.error(err));
      }
    } catch (err: unknown) {
      console.error(err);
    }
  }

  private handleChunkLoadErrors(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((errorEvent) => {
        if (
          this.online.isAppOnline &&
          errorEvent.error instanceof Error &&
          (errorEvent.error?.name?.includes("ChunkLoadError") || errorEvent.error?.message?.includes("ChunkLoadError"))
        ) {
          window.location.assign(errorEvent.url);
        }
      });
  }

  /** Sets the User's preferred language after their session begins */
  private watchAndSetUserLang(): void {
    this.session.userSession$
      .pipe(
        distinctUntilChanged((previous, current) => previous?.email === current?.email),
        map((userSession) => userSession?.preferences?.preferredLanguage ?? null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async (preferredLanguage): Promise<void> => {
        if (preferredLanguage) {
          const appLang = langTagToAppLang(preferredLanguage);
          if (appLang && appLang !== this.translate.getCurrentLang()) {
            try {
              await firstValueFrom(this.translate.use(appLang));
              this.cdr.markForCheck();
            } catch (err: unknown) {
              console.error(err);
            }
          }
          // Save missing user preference
        } else if (this.auth.isAuthenticated) {
          const guestLangSetting = this.cookieStorage.guestLang() || this.translate.getCurrentLang();
          if (guestLangSetting) {
            const guestLang = langTagToAppLang(guestLangSetting);
            if (guestLang) {
              try {
                await firstValueFrom(this.userSettings.setUserPreferredLanguage(guestLang));
                this.cdr.markForCheck();
              } catch (err: unknown) {
                console.error(err);
              }
            }
          }
        }
      });
  }

  private watchRouteChanges(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.cdr.markForCheck();
        const { description, noIndex } = this.getMetaDataFromRouteBranch();
        this.handleDescription(description ?? null);
        this.handleNoIndexTag(noIndex ?? null);
      });

    // Ensure that there's something on the main router outlet after navigation error
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async (errorEvent) => {
        if (!this.router.routerState.root.firstChild && !errorEvent.url.includes("error")) {
          const err: unknown = errorEvent.error;
          await this.router.navigate(["/error"], {
            queryParams: {
              message: err instanceof Error && err.message ? err.message : [],
              statusText: "NavigationError",
            },
          });
        }
      });
  }

  private watchLangChange(): void {
    this.setLangTags();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.cdr.markForCheck();
      this.setLangTags();
      this.handleDescription(this.getMetaDataFromRouteBranch().description ?? null);
    });
  }

  private setLangTags(): void {
    const currentLang = this.translate.getCurrentLang()!;
    this.document.documentElement.lang = getHtmlLangValue(currentLang);
    this.document.dir = getLanguageDirection(currentLang);
    sentrySetTag(SentryTag.CurrentLang, currentLang);
    sentrySetTag(SentryTag.NavigatorLanguage, navigator.language);
    sentrySetTag(SentryTag.NavigatorLanguageList, navigator.languages?.join(","));
    sentrySetTag(SentryTag.DocumentDirection, this.document.dir);
  }

  /** Translate and change the route's description on the page's header */
  private handleDescription(value: string | null): void {
    const currentDescriptionKey: string = value && typeof value === "string" && value.trim() ? value : this.defaultDescriptionKey;
    const content = currentDescriptionKey ? (this.translate.instant(currentDescriptionKey) ?? currentDescriptionKey) : currentDescriptionKey;
    if (
      typeof content === "string" &&
      currentDescriptionKey !== content &&
      content.trim() &&
      this.meta.getTag("name='description'")?.content !== content
    ) {
      this.meta.updateTag({ name: "description", content });
    }
  }

  /** Remove or add the "noindex" value from/on the Robots meta tag */
  private handleNoIndexTag(isNoIndex: boolean | null): void {
    const robotsTagList = this.meta.getTags("name='robots'") ?? [];
    const contentSet: Set<string> = new Set(robotsTagList.flatMap((x) => x.content?.split(/\s*,\s*/) ?? []));
    if (isNoIndex && !contentSet.has("noindex")) {
      contentSet.add("noindex");
      // Remove and re-create element for better browser support
      robotsTagList.forEach((tag) => this.meta.removeTagElement(tag));
      this.meta.updateTag({ name: "robots", content: Array.from(contentSet.values()).join(", ") });
    } else if (!isNoIndex && contentSet.has("noindex")) {
      contentSet.delete("noindex");
      if (contentSet.size > 1) {
        // Remove and re-create element for better browser support
        robotsTagList.forEach((tag) => this.meta.removeTagElement(tag));
        this.meta.updateTag({ name: "robots", content: Array.from(contentSet.values()).join(", ") });
      } else {
        robotsTagList.forEach((tag) => this.meta.removeTagElement(tag));
      }
    }
  }

  private getMetaDataFromRouteBranch(): { description?: string; noIndex?: boolean } {
    let route: ActivatedRoute | null | undefined = this.router.routerState.root;
    let description: string | undefined = undefined;
    let noIndex: boolean | undefined = undefined;
    do {
      const data = <AppRouteData>route.snapshot.data;
      description = data?.description ?? description;
      noIndex = data?.noIndex ?? noIndex;
    } while ((route = route.firstChild));
    return { description, noIndex };
  }

  /** Sets the alt text for the required indicator */
  private setAltRequiredIndicator(): void {
    runInInjectionContext(this.injector, () => {
      const localName = this.elementRef.nativeElement.localName;
      const currentStyleSheet = signal<HTMLStyleElement | null>(null);

      toObservable(currentStyleSheet)
        .pipe(
          startWith(null),
          pairwise(),
          takeUntilDestroyed(this.destroyRef),
          finalize(() => currentStyleSheet()?.remove())
        )
        .subscribe(([previous, current]) => {
          this.cdr.markForCheck();
          if (current) this.document.head.appendChild(current);
          if (previous) previous.remove();
        });

      const requiredAltKey = "REQUIRED.ALT";
      this.translate
        .stream(requiredAltKey)
        .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
        .subscribe((translation: unknown) => {
          const styleElement = this.document.createElement("style");
          const label = typeof translation === "string" && translation !== requiredAltKey ? `${translation}` : "*";
          styleElement.innerHTML = `${localName} { --required-indicator-alt: '${label}'; }`;
          currentStyleSheet.set(styleElement);
        });
    });
  }

  private outdatedJavascriptVersionWarning(): void {
    // Browser doesn't implement es2022, the app's target
    // This is checked on polyfills.ts before adding the "at" polyfill
    if (!Array.prototype.at || this.document.body?.getAttribute("data-is-legacy") === "true") {
      this.toast.showDismissibleToast("WARNING.JAVASCRIPT_VERSION", ToastType.Warning, { timer: Infinity });
    }
  }
}
